import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Maximize2, Minimize2, PictureInPicture2, Volume2 } from 'lucide-react';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useEvergreenVideoSource } from '@/hooks/useEvergreenVideoSource';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';
import {
  enterPictureInPicture,
  exitPictureInPicture,
  exitVideoFullscreen,
  getFullscreenElement,
  isInPictureInPicture,
  isMobileViewer,
  isVideoPlaying,
  requestVideoFullscreen,
  supportsElementFullscreen,
  supportsPictureInPicture,
} from '@/lib/videoPresentation';
import { clampEvergreenPosition, resolveEvergreenSeekCorrection } from '@/lib/videoSeekGuard';
import {
  evergreenControlsBoxClassName,
  evergreenVideoBoxClassName,
} from '@/lib/evergreenFrameLayout';
import { evergreenTestClockSeconds } from '@/lib/evergreenTestRun';
import { cn } from '@/lib/utils';

// Cadence de relevé de la position, en plus de `timeupdate` (espacé jusqu'à 250 ms selon le navigateur).
const POSITION_POLL_MS = 250;

type EvergreenTestPlayerProps = {
  videoUrl: string;
  fallbackVideoUrl: string | null;
  /** Instant local de démarrage du test : sert à reprendre et à recaler, pas à la timeline. */
  startMs: number;
  durationSeconds: number;
  visualIdentity: WorkspaceVisualIdentity;
  hasVisibleInteraction: boolean;
  /** Position de lecture de la vidéo, qui pilote la timeline. */
  onPositionChange: (positionMs: number) => void;
  onEnded: () => void;
};

/**
 * Lecteur de la salle de test : même cadre, mêmes contrôles et même lecture que le spectateur
 * Evergreen réel (copie de sa logique), plus un verrou sur la vitesse de lecture.
 */
export default function EvergreenTestPlayer({
  videoUrl,
  fallbackVideoUrl,
  startMs,
  durationSeconds,
  visualIdentity,
  hasVisibleInteraction,
  onPositionChange,
  onEnded,
}: EvergreenTestPlayerProps) {
  const { t } = useWebinarT('webinars');
  // Les libellés des contrôles vidéo vivent dans `common`, partagés avec le player du direct.
  const { t: tCommon } = useWebinarT('common');
  const webinarLng = useWebinarLng();
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  /** Seeks demandés par le code et pas encore consommés, pour ne pas les prendre pour un scrub. */
  const programmaticSeeksRef = useRef(0);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
    onEndedRef.current = onEnded;
  }, [onPositionChange, onEnded]);

  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [canUsePip, setCanUsePip] = useState(false);
  const [isInPip, setIsInPip] = useState(false);
  const [canUseFullscreen, setCanUseFullscreen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [canHover] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );

  const getElapsedSeconds = useCallback(
    () => evergreenTestClockSeconds(startMs, Date.now(), durationSeconds),
    [startMs, durationSeconds],
  );

  /** Seul point d'entrée pour déplacer la lecture, pour que le filet anti-avance l'ignore. */
  const seekTo = useCallback((video: HTMLVideoElement, target: number) => {
    if (!Number.isFinite(target)) return;
    if (Math.abs(video.currentTime - target) < 0.05) return;
    programmaticSeeksRef.current += 1;
    video.currentTime = target;
  }, []);

  const { activeUrl, hasFailed } = useEvergreenVideoSource({
    enabled: true,
    videoRef,
    primaryUrl: videoUrl,
    fallbackUrl: fallbackVideoUrl,
    getResumePosition: getElapsedSeconds,
    seekTo,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = isMuted;

    const tryPlay = () => {
      void video.play().catch(() => {
        video.muted = true;
        void video.play().catch(() => {});
      });
    };
    const seekAndPlay = () => {
      seekTo(video, clampEvergreenPosition(getElapsedSeconds(), video.duration));
      tryPlay();
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekAndPlay();
    } else {
      video.addEventListener('loadedmetadata', seekAndPlay, { once: true });
    }
    video.addEventListener('canplay', tryPlay);
    const retryTimer = setTimeout(() => {
      if (video.paused) tryPlay();
    }, 700);

    return () => {
      video.removeEventListener('loadedmetadata', seekAndPlay);
      video.removeEventListener('canplay', tryPlay);
      clearTimeout(retryTimer);
    };
  }, [getElapsedSeconds, activeUrl, isMuted, seekTo]);

  // Recale la lecture sur l'horloge du test puis relance (retour d'onglet, sortie de plein écran).
  const resyncPlayback = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    const video = videoRef.current;
    if (!video) return;
    video.muted = isMuted;
    seekTo(video, clampEvergreenPosition(getElapsedSeconds(), video.duration));
    void video.play().catch(() => {
      video.muted = true;
      void video.play().catch(() => {});
    });
  }, [isMuted, getElapsedSeconds, seekTo]);

  // Filet anti-avance : en plein écran natif ou en mini-fenêtre, le navigateur réaffiche sa barre.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onSeeked = () => {
      if (programmaticSeeksRef.current > 0) {
        programmaticSeeksRef.current -= 1;
        return;
      }
      const target = resolveEvergreenSeekCorrection({
        currentTime: video.currentTime,
        expectedTime: getElapsedSeconds(),
        duration: video.duration,
      });
      if (target === null) return;
      seekTo(video, target);
      void video.play().catch(() => {});
    };
    video.addEventListener('seeked', onSeeked);
    return () => video.removeEventListener('seeked', onSeeked);
  }, [activeUrl, getElapsedSeconds, seekTo]);

  // Verrou de vitesse propre à la salle de test : la timeline suit la lecture, une vitesse x2
  // ferait défiler offres et messages deux fois trop vite.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const lockRate = () => {
      if (video.playbackRate !== 1) video.playbackRate = 1;
      if (video.defaultPlaybackRate !== 1) video.defaultPlaybackRate = 1;
    };
    lockRate();
    video.addEventListener('ratechange', lockRate);
    return () => video.removeEventListener('ratechange', lockRate);
  }, [activeUrl]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const video = videoRef.current;
      if (document.visibilityState === 'hidden') {
        if (
          video &&
          isMobileViewer() &&
          supportsElementFullscreen(frameRef.current) &&
          isVideoPlaying(video)
        ) {
          void enterPictureInPicture(video);
        }
        return;
      }
      if (video) void exitPictureInPicture(video);
      resyncPlayback();
    };
    const onFocus = () => resyncPlayback();
    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('focus', onFocus);
    resyncPlayback();
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('focus', onFocus);
    };
  }, [resyncPlayback]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const report = () => {
      if (video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      if (!Number.isFinite(video.currentTime)) return;
      onPositionChangeRef.current(Math.max(0, video.currentTime * 1000));
    };
    const handleEnded = () => onEndedRef.current();
    report();
    video.addEventListener('timeupdate', report);
    video.addEventListener('seeked', report);
    video.addEventListener('ended', handleEnded);
    const intervalId = window.setInterval(report, POSITION_POLL_MS);
    return () => {
      video.removeEventListener('timeupdate', report);
      video.removeEventListener('seeked', report);
      video.removeEventListener('ended', handleEnded);
      window.clearInterval(intervalId);
    };
  }, [activeUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let waitingTimer: ReturnType<typeof setTimeout> | null = null;
    const clearWaitingTimer = () => {
      if (waitingTimer) {
        clearTimeout(waitingTimer);
        waitingTimer = null;
      }
    };
    const settle = () => {
      clearWaitingTimer();
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) setIsVideoLoading(false);
    };
    // Spinner réaffiché seulement si le rebuffer dure, pour éviter le flash au recalage.
    const markLoadingDeferred = () => {
      if (waitingTimer) return;
      waitingTimer = setTimeout(() => {
        waitingTimer = null;
        setIsVideoLoading(true);
      }, 400);
    };
    video.addEventListener('playing', settle);
    video.addEventListener('canplay', settle);
    video.addEventListener('timeupdate', settle);
    video.addEventListener('waiting', markLoadingDeferred);
    return () => {
      clearWaitingTimer();
      video.removeEventListener('playing', settle);
      video.removeEventListener('canplay', settle);
      video.removeEventListener('timeupdate', settle);
      video.removeEventListener('waiting', markLoadingDeferred);
    };
  }, [activeUrl]);

  useEffect(() => {
    return () => {
      if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    };
  }, []);

  /** Une interaction apparaît : on sort du plein écran pour qu'elle reste visible. */
  useEffect(() => {
    if (!hasVisibleInteraction) return;
    const video = videoRef.current;
    if (video) exitVideoFullscreen(video);
  }, [hasVisibleInteraction]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const syncCapabilities = () => {
      setCanUsePip(
        isMobileViewer() &&
          supportsPictureInPicture(video) &&
          supportsElementFullscreen(frameRef.current),
      );
      setIsInPip(isInPictureInPicture(video));
      setCanUseFullscreen(supportsElementFullscreen(frameRef.current));
    };
    // Les contrôles n'apparaissent qu'une fois la vidéo chargée : inutile de mesurer avant.
    video.addEventListener('loadedmetadata', syncCapabilities);
    const onEnterPip = () => setIsInPip(true);
    const onLeavePip = () => {
      setIsInPip(false);
      resyncPlayback();
    };
    const onWebkitPresentationChange = () => {
      const inPip = isInPictureInPicture(video);
      setIsInPip(inPip);
      if (!inPip) resyncPlayback();
    };
    const onFullscreenChange = () => {
      const inFullscreen = !!getFullscreenElement();
      setIsFullscreen(inFullscreen);
      if (!inFullscreen) resyncPlayback();
    };
    video.addEventListener('enterpictureinpicture', onEnterPip);
    video.addEventListener('leavepictureinpicture', onLeavePip);
    video.addEventListener('webkitpresentationmodechanged', onWebkitPresentationChange);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    video.addEventListener('webkitendfullscreen', resyncPlayback);
    return () => {
      video.removeEventListener('loadedmetadata', syncCapabilities);
      video.removeEventListener('enterpictureinpicture', onEnterPip);
      video.removeEventListener('leavepictureinpicture', onLeavePip);
      video.removeEventListener('webkitpresentationmodechanged', onWebkitPresentationChange);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      video.removeEventListener('webkitendfullscreen', resyncPlayback);
    };
  }, [activeUrl, resyncPlayback]);

  const handleActivateSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    setIsMuted(false);
  };

  const revealControls = () => {
    setShowControls(true);
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    hideControlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  };
  const showControlsOnHover = () => {
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    setShowControls(true);
  };
  const hideControlsOnHover = () => {
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    setShowControls(false);
  };

  const togglePictureInPicture = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isInPictureInPicture(video)) void exitPictureInPicture(video);
    else void enterPictureInPicture(video);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isFullscreen) {
      exitVideoFullscreen(video);
      return;
    }
    if (hasVisibleInteraction || !supportsElementFullscreen(frameRef.current)) return;
    requestVideoFullscreen(video, frameRef.current);
  };

  return (
    <>
      <div
        ref={frameRef}
        className={cn(
          'w-full max-h-full lg:h-full min-w-0 min-h-0 relative overflow-hidden rounded-none lg:rounded-2xl aspect-video',
          'compact-landscape:h-full compact-landscape:rounded-2xl',
          '[&:fullscreen]:aspect-auto [&:fullscreen]:h-full [&:fullscreen]:max-h-none [&:fullscreen]:rounded-none',
        )}
        style={{ backgroundColor: visualIdentity.backgroundColor ?? '#FFFFFF' }}
        onClick={() => {
          if (!canHover) revealControls();
        }}
        onMouseEnter={() => {
          if (canHover) showControlsOnHover();
        }}
        onMouseMove={() => {
          if (canHover && isFullscreen) revealControls();
        }}
        onMouseLeave={() => {
          if (canHover) hideControlsOnHover();
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full max-h-full aspect-video flex items-center justify-center">
            <div className={evergreenVideoBoxClassName(isFullscreen)}>
              <video
                ref={videoRef}
                src={activeUrl}
                className="absolute inset-0 w-full h-full object-contain origin-top-left scale-[1.005]"
                playsInline
                controls={false}
                controlsList="nodownload noremoteplayback"
                onContextMenu={(e) => e.preventDefault()}
                autoPlay
                muted={isMuted}
              >
                <track kind="captions" srcLang={webinarLng} label={t('live.noCaptionsTrack')} />
              </video>
            </div>
          </div>
        </div>
        {hasFailed && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/60 px-6 text-center">
            <p className="text-white text-sm md:text-base font-medium">
              {t('live.evergreenVideoUnavailable')}
            </p>
          </div>
        )}
        {isVideoLoading && !hasFailed && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 pointer-events-none">
            <Loader2 className="animate-spin size-10 text-white" />
            <p className="text-white text-sm md:text-base font-medium">
              {t('live.loadingInProgress')}
            </p>
          </div>
        )}
        {showControls && !isMuted && !isVideoLoading && !hasVisibleInteraction && (
          <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
            <div className="w-full max-h-full aspect-video flex items-center justify-center">
              <div className={evergreenControlsBoxClassName(isFullscreen)}>
                <div className="absolute bottom-3 right-3 flex items-center gap-2 pointer-events-auto">
                  {canUsePip && (
                    <button
                      type="button"
                      onClick={togglePictureInPicture}
                      aria-label={tCommon('liveSettings.pictureInPicture')}
                      title={tCommon('liveSettings.pictureInPicture')}
                      aria-pressed={isInPip}
                      className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                    >
                      <PictureInPicture2 className="size-5 text-white" aria-hidden />
                    </button>
                  )}
                  {canUseFullscreen && (
                    <button
                      type="button"
                      onClick={toggleFullscreen}
                      aria-label={tCommon(
                        isFullscreen ? 'liveSettings.exitFullscreen' : 'liveSettings.fullscreen',
                      )}
                      title={tCommon(
                        isFullscreen ? 'liveSettings.exitFullscreen' : 'liveSettings.fullscreen',
                      )}
                      className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                    >
                      {isFullscreen ? (
                        <Minimize2 className="size-5 text-white" aria-hidden />
                      ) : (
                        <Maximize2 className="size-5 text-white" aria-hidden />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isMuted && !isVideoLoading && (
        <button
          type="button"
          onClick={handleActivateSound}
          className="absolute hover:bg-black/80 cursor-pointer flex-col gap-6 left-1/2 flex flex-wrap items-center justify-center -translate-x-1/2 z-10 top-1/2 -translate-y-1/2 w-full md:w-xl h-full lg:h-80 shadow-2xl backdrop-blur-sm overflow-x-auto bg-black/70 rounded-3xl p-2 md:p-8"
        >
          <div
            className="flex items-center justify-center p-3 rounded-xl"
            style={{
              backgroundColor: visualIdentity.secondaryBackgroundColor
                ? `${visualIdentity.secondaryBackgroundColor}20`
                : '#ffffff',
            }}
          >
            <Volume2
              className="size-9"
              style={{ color: visualIdentity.secondaryBackgroundColor ?? '#ffffff' }}
            />
          </div>
          <p className="text-white text-xl md:text-3xl font-bold wrap-break-word w-full">
            {t('live.youtube.unmute.title')}
          </p>
          <p className="text-white text-xs md:text-sm font-normal wrap-break-word w-full">
            {t('live.youtube.unmute.subtitle')}
          </p>
        </button>
      )}
    </>
  );
}
